import React, { useEffect, useState } from "react";
import { Calendar } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { Loader2 } from "lucide-react";
import { SafeAvailabilityRule } from "@/app/types";
import { Range } from "react-date-range";

interface CalendarDatesProps {
  value: Range;
  onChange?: (value: Range) => void;
  availabilityRules: SafeAvailabilityRule[];
  disabledDates: Date[];
  minDate?: Date;
  onMonthChange: (month: number, year: number) => void;
  loading?: boolean;
  currentMonth: number;
  currentYear: number;
}

const CalendarDates: React.FC<CalendarDatesProps> = ({
  value,
  onChange,
  availabilityRules,
  disabledDates,
  minDate,
  onMonthChange,
  loading = false,
  currentMonth,
  currentYear,
}) => {
  const [availableDates, setAvailableDates] = useState<Date[]>([]);

  // Generate available dates based on availabilityRules
  useEffect(() => {
    if (!availabilityRules || availabilityRules.length === 0) {
      setAvailableDates([]);
      return;
    }

    const dates: Date[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Generate dates for the current and next month
    const endDate = new Date(currentYear, currentMonth + 1, 0); // Last day of next month

    // Process each rule
    availabilityRules.forEach((rule) => {
      const ruleStartDate = new Date(rule.startDate);
      const ruleEndDate = new Date(rule.endDate);

      // Skip if rule is completely in the past
      if (ruleEndDate < today) return;

      // Get excluded dates for this rule
      const excludedDatesSet = new Set(
        rule.excludedDates?.map(ed => new Date(ed.date).toDateString()) || []
      );

      // Generate all dates within the rule's range
      const currentDate = new Date(
        Math.max(ruleStartDate.getTime(), today.getTime())
      );

      while (currentDate <= ruleEndDate && currentDate <= endDate) {
        // Check if day of week is included in rule
        const dayOfWeek = currentDate.getDay() === 0 ? 7 : currentDate.getDay();

        if (
          rule.daysOfWeek.includes(dayOfWeek) &&
          !excludedDatesSet.has(currentDate.toDateString())
        ) {
          dates.push(new Date(currentDate));
        }

        // Move to next day
        currentDate.setDate(currentDate.getDate() + 1);
      }
    });

    setAvailableDates(dates);
  }, [availabilityRules, currentMonth, currentYear]);

  const handleDateChange = (date: Date) => {
    const isAvailable = availableDates?.some(
      (availableDate) => availableDate.toDateString() === date.toDateString()
    );

    if (isAvailable && onChange && date) {
      const newRange: Range = {
        startDate: date,
        endDate: date,
        key: "selection",
      };
      onChange(newRange);
    }
  };

  const handleShownDateChange = (date: Date) => {
    onMonthChange(date.getMonth() + 1, date.getFullYear());
  };

  const renderDayContent = (date: Date) => {
    const isAvailable = availableDates?.some(
      (availableDate) => availableDate.toDateString() === date.toDateString()
    );
    const isPastDate = date < new Date();

    const isSelected =
      value.startDate && date.toDateString() === value.startDate.toDateString();

    const isDisabled = !isAvailable || disabledDates.some(
      (disabledDate) => disabledDate.toDateString() === date.toDateString()
    );

    return (
      <div
        style={{
          height: "100%",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: isSelected ? "8px" : "0",
          backgroundColor: isSelected ? "#0e7490" : "transparent",
          color: isSelected ? "white" : isDisabled ? "gray" : isPastDate ? "gray" : "black",
          cursor: isDisabled ? "not-allowed" : "pointer",
        }}
      >
        {date.getDate()}
      </div>
    );
  };

  return (
    <div className="relative w-full max-w-4xl mx-auto">
      {loading && (
        <div className="absolute inset-0 bg-white/80 z-10 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-cyan-600" />
        </div>
      )}
      <div className="calendar-wrapper">
        <style jsx global>{`
          .calendar-wrapper .rdrCalendarWrapper {
            width: 100%;
            font-size: 16px;
          }
          .calendar-wrapper .rdrMonth {
            width: 100%;
          }
          .calendar-wrapper .rdrDayNumber {
            font-size: 16px;
          }
          .calendar-wrapper .rdrDay {
            height: 50px;
          }
          .calendar-wrapper .rdrDayToday .rdrDayNumber span:after {
            background: #0e7490;
          }
          .calendar-wrapper .rdrDayDisabled {
            background-color: transparent;
          }
          .calendar-wrapper .rdrDayDisabled span {
            color: #d4d4d4;
          }
          .calendar-wrapper .rdrMonthAndYearWrapper {
            padding-top: 10px;
            padding-bottom: 10px;
            height: auto;
          }
          .calendar-wrapper .rdrNextPrevButton {
            margin: 0;
          }
          .calendar-wrapper .rdrDateDisplayWrapper {
            display: none;
          }
        `}</style>
        <Calendar
          date={value.startDate || new Date()}
          onChange={handleDateChange}
          minDate={minDate || new Date()}
          dayContentRenderer={renderDayContent}
          weekStartsOn={1}
          shownDate={new Date(currentYear, currentMonth - 1)}
          onShownDateChange={handleShownDateChange}
          color="#0e7490"
          className="rounded-md"
        />
      </div>
    </div>
  );
};

export default CalendarDates;